import type { ActivityEntry, Request } from '@/lib/dd-queue/types'
import type { ResolvedSubject } from '@/lib/dd-queue/resolveSubject'

/**
 * Compact relative display for the 70px `.fr-activity-time` column
 * (reference parity: "just now" / "2h ago" / "3d ago" — not toLocaleString).
 */
export function formatActivityTime(t: string, nowMs: number = Date.now()): string {
  const trimmed = t.trim()
  if (!trimmed) return trimmed
  if (/^(just now|\d+m ago|\d+h ago|\d+d ago)$/i.test(trimmed)) return trimmed

  const ms = Date.parse(trimmed)
  if (!Number.isFinite(ms)) return trimmed

  const diff = Math.max(0, nowMs - ms)
  const sec = Math.floor(diff / 1000)
  if (sec < 60) return 'just now'
  const min = Math.floor(sec / 60)
  if (min < 60) return `${min}m ago`
  const hr = Math.floor(min / 60)
  if (hr < 48) return `${hr}h ago`
  const day = Math.floor(hr / 24)
  return `${day}d ago`
}

/** Full chronological activity — reference ActivityLogCard.tsx L18 + SubjectPeek.tsx L39.
 * BUG-STG-003-TAIL: uuid'd (server-backed) lines use activityHead ONLY — never the
 * client-synthesized Origin seed on req.activity (viewer/payload +"just now" phantom).
 * Fixture / no-uuid rows still append req.activity (fixture submitter + ts).
 */
export function mergeActivityLog(
  req: Request,
  resolved: Pick<ResolvedSubject, 'activityHead'>
): ActivityEntry[] {
  const head = resolved.activityHead ?? []
  const seed = req.submissionUuid ? [] : (req.activity ?? [])
  return [...head, ...seed].map((a) => ({
    ...a,
    t: formatActivityTime(a.t),
  }))
}
